// TaskOverViewCheckInCampaign.tsx

import { useSlice } from "@bridge-applications/provider-kit";
import "./TaskOverViewCheckInCampaign.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowUpRightFromSquare,
    faPlus,
    faXmark,
} from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import WldIcon from "../../../../../assets/icons/wld-icon.svg?react";
import GoldIcon from "../../../../../assets/icons/gold-icon.svg?react";
import UsdcIcon from "../../../../../assets/icons/usdc-icon.svg?react";
import OroLogoImg from "../../../../../assets/images/coins/oro-icon.webp";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import BgStar from "../../../../../assets/icons/bg-star.svg?react";
import CheckInDayGrid from "./comps/CheckInDayGrid/CheckInDayGrid";
import Button3D from "../../../Button3D/Button3D";
import VoucherFaucetABI from "../../../../../abi/MultiTokenVoucherFaucet.json";
import { claim } from "../../../../../claim";
import {
    // ApiChiProgressInitReqSchema,
    ApiChiProgressInitResSchema,
    // ApiChiProgressStatusReqSchema,
    ApiChiProgressStatusResSchema,
    ApiChiCompleteResSchema,
    ApiChiStartReqSchema,
    ApiChiStartResSchema,
} from "@bridge-applications/eh-types";
import CircularLoader from "../../../CircularLoadingIndicator/CircularLoadingIndicator";
import { API_BASE } from "../../../../../state/config";
import { ExtendedFeedItemSchema } from "../../../../../schemas/slices/FeedSliceSchema";
import { throwUnhandledError } from "../../../../../logic/helpers/throwUnhandledError.ts";

type LanguageCode = "en" | "es" | "id" | "pt-br" | "th";
interface TaskOverViewCheckInCampaignProps {
    taskId: string;
    onClose: () => void;
}

const API_TASK_START = `${API_BASE}/api/v1/chi/start`;
const API_TASK_PROGRESS_INIT = `${API_BASE}/api/v1/chi/progress/init`;
const API_TASK_PROGRESS_STATUS = `${API_BASE}/api/v1/chi/progress/status`;

// Fallback only. Primary app_id should come from task.raw.partner_id.
// const DEFAULT_WORLD_MINI_APP_ID = "app_443bd39e83f7ab076b200e630c70c772";

const OPTIMISTIC_CHECK_IN_CAMPAIGN_IDS = new Set<string>([
    "10ac0517-a8de-4b6c-af65-0a8ab3303f4e",
    "fb8a2fbe-ff53-42f1-aa77-e94de53dc358",
    "f3445c73-65d0-4d65-b0f2-3aede7a99825",
    "63d3a461-6bf1-4a92-8a8b-0780cc69c001",
    "5a0ff9f7-573e-4217-a86f-7bf941e8e2df",
    "8de8bda0-38e1-4980-8a2b-83f40aa717e6",
    "b1b3e02d-38ba-4e6b-94da-b9fa4980312f",
    "3b7c0016-1e2d-4eed-a443-716c798b2f33",
    "10f5201b-646c-4be4-9525-cdf385e3b4a5",
]);
const DEFAULT_WORLD_MINI_APP_ID = "---";

const isWorldMiniAppId = (v: unknown): v is string =>
    typeof v === "string" && /^app_[a-zA-Z0-9]{32}$/.test(v);

const resolveWorldMiniAppId = (partnerId: unknown): string =>
    isWorldMiniAppId(partnerId) ? partnerId : DEFAULT_WORLD_MINI_APP_ID;

const buildPartnerUrl = (partnerId: unknown, shortId: string) => {
    const appId = resolveWorldMiniAppId(partnerId);
    return `https://world.org/mini-app?app_id=${encodeURIComponent(
        appId,
    )}&path=${encodeURIComponent(`/?eh-id=${shortId}`)}`;
};

const buildPartnerBaseUrl = (partnerId: unknown) => {
    const appId = resolveWorldMiniAppId(partnerId);
    return `https://world.org/mini-app?app_id=${encodeURIComponent(
        appId,
    )}&path=${encodeURIComponent("/")}`;
};

const API_TASK_COMPLETE = `${API_BASE}/api/v1/chi/complete`;
// Same faucet used elsewhere in the app (CompletionPage)
const VOUCHER_FAUCET_ADDRESS =
    (import.meta as any)?.env?.VITE_VOUCHER_FAUCET_ADDRESS ??
    "0x37b553e5cfb827FC486c70D8dcD6c17Caab76620";

// minimal local shape for rewards to avoid import/type friction
type AnyReward = {
    isPrimaryReward: boolean;
    type: string;
    reward: { unit: string; amount: number };
};

export default function TaskOverViewCheckInCampaign({
    taskId,
    onClose,
}: TaskOverViewCheckInCampaignProps) {
    // Write LS resume flag with TTL so MainPage can reopen this drawer after reload
    const markResume = () => {
        try {
            localStorage.setItem(
                "eh_resume_bottom_drawer_v1",
                JSON.stringify({
                    t: "chi",
                    taskId,
                    at: Date.now(),
                    ttlMs: 15 * 60 * 1000,
                }),
            );
        } catch (error) {
            throwUnhandledError(error, "Unable to write check-in resume flag");
        }
    };
    type ExtendedFeedItem = z.infer<typeof ExtendedFeedItemSchema>;
    type ChiItem = Extract<
        ExtendedFeedItem,
        { productType: "PARTNER_CAMPAIGN_CHI" }
    >;

    const { i18n, t } = useTranslation();
    const lang = i18n.language as LanguageCode;
    // const { state: taskState, setSlice: setTaskSlice } = useSlice("taskSlice");
    const { state: feedState, setSlice: setFeedSlice } = useSlice("feedSlice");
    const [task, setTask] = useState<ChiItem | undefined>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [locallyStarted, setLocallyStarted] = useState(false);
    const [progress, setProgress] = useState<{
        code?: string;
        expiresAt?: number;
        status?: "PENDING" | "ACKED" | "APPLIED" | "EXPIRED";
        initiated?: boolean; // user tapped Open flow
    } | null>(null);
    const [startedVerification, setStartedVerification] = useState(false);
    const STORAGE_KEY = `eh_progress_${taskId}`;
    const pollRef = useRef<number | null>(null);
    const [, forceTick] = useState(0); // for TTL countdown rerenders
    const tickRef = useRef<number | null>(null);
    const prefetchingRef = useRef(false);
    const refreshIntervalRef = useRef<number | null>(null);
    const openDelayRef = useRef<number | null>(null);
    const descRef = useRef<HTMLParagraphElement>(null);
    const [descTopFade, setDescTopFade] = useState(0);
    const [descBottomFade, setDescBottomFade] = useState(0);

    const _task = feedState.items.find(
        (t): t is ChiItem =>
            t.raw.id === taskId && t.productType === "PARTNER_CAMPAIGN_CHI",
    );
    console.log("feedState", feedState, "taskId", taskId);
    console.log("task1", _task);

    // cache a bearer token to avoid stalled auth handshakes on resume
    const tokenRef = useRef<string | null>(null);

    const getAccessToken = async (): Promise<string> => {
        if (tokenRef.current) return tokenRef.current;
        const t = await WorldAuthClient.getAccessTokenSilently();
        tokenRef.current = t.accessToken;
        return tokenRef.current;
    };

    // Local overlay of days/completion so UI updates instantly on APPLIED
    const [localDaysCheckedIn, setLocalDaysCheckedIn] = useState<
        number[] | undefined
    >(undefined);
    const [localCompleted, setLocalCompleted] = useState<boolean | undefined>(
        undefined,
    );
    const [openingSoon, setOpeningSoon] = useState(false);
    // Dedicated flag that controls "Verifying..." UI until status != PENDING
    const [isVerifying, setIsVerifying] = useState(false);
    // Track the last opened URL for quick re-opening
    const [lastOpenedUrl, setLastOpenedUrl] = useState<string | null>(null);

    useEffect(() => {
        const foundTask = feedState.items.find(
            (t): t is ChiItem =>
                t.raw.id === taskId && t.productType === "PARTNER_CAMPAIGN_CHI",
        );
        if (foundTask) setTask(foundTask);
    }, [taskId, feedState.items]);

    // In your data model, this is the World mini-app app_id for this campaign.
    const partnerAppId = task?.raw?.partner_id;
    const shouldOptimisticallyApplyCheckIn =
        !!task && OPTIMISTIC_CHECK_IN_CAMPAIGN_IDS.has(task.itemId);

    // Adapter: normalize legacy "tc" fields from the new relation/raw
    const tc = useMemo(
        () =>
            task
                ? {
                      status: task.relation?.status,
                      // normalize to epoch ms for date math
                      started_at: task.relation?.startedAt
                          ? Date.parse(task.relation.startedAt)
                          : undefined,
                      completedAt: task.relation?.completedAt
                          ? Date.parse(task.relation.completedAt)
                          : undefined,
                      timezone: undefined as string | undefined,
                      // always numbers; keep undefined if not present
                      days_checked_in:
                          task.relation?.daysCheckedIn?.map((n: any) =>
                              Number(n),
                          ) ?? undefined,
                      // prefer raw, then relation fallback
                      day_amount:
                          task.raw.check_in_campaign?.required_days ??
                          task.relation?.requiredDays ??
                          undefined,
                      day_tolerance:
                          task.raw.check_in_campaign?.tolerance_days ??
                          task.relation?.toleranceDays ??
                          undefined,
                  }
                : undefined,
        [task],
    );

    // Keep local mirrors in sync when task changes (unless we already adjusted locally)
    useEffect(() => {
        if (!task) return;
        if (task.productType !== "PARTNER_CAMPAIGN_CHI") {
            return;
        }
        if (!task.relation?.completedAt) {
            // still mirror days if present
            if (localDaysCheckedIn == null) {
                setLocalDaysCheckedIn(
                    task.relation?.daysCheckedIn ?? undefined,
                );
            }
        }
        if (localDaysCheckedIn == null) {
            setLocalDaysCheckedIn(task.relation?.daysCheckedIn ?? undefined);
        }
        if (localCompleted == null) {
            const st = task.relation?.status;
            setLocalCompleted(
                !!task.relation?.completedAt ||
                    st === "COMPLETED" ||
                    st === "FAILED",
            );
        }
    }, [task]);

    // Clear pollers on unmount
    useEffect(() => {
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            if (tickRef.current) {
                clearInterval(tickRef.current);
                tickRef.current = null;
            }
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
            if (openDelayRef.current) {
                clearTimeout(openDelayRef.current);
                openDelayRef.current = null;
            }
            setOpeningSoon(false);
        };
    }, []);

    // ----- helpers

    // put near other helpers
    const resumePolling = () => {
        // Never resume if the server says the task hasn't started (server authoritative)
        if (!enrolled || serverSaysNotStarted) return;
        if (!progress?.code || hasFinished || hasCheckedInToday) return;
        // kill any possibly-frozen interval
        stopPolling();
        // start a fresh interval; pass expiresAt so we preserve it on first setProgress
        startPollingStatus(progress.code, progress.expiresAt);
        if (!tickRef.current) beginTick();
        setIsVerifying(true);
    };

    // Handshake popup: open partner app directly with a delay
    const openViaHandshake = (
        targetUrl: string,
        delayMs = 1500,
        afterNavigationAttempt?: () => void,
    ) => {
        setOpeningSoon(true);
        // ensure resume flag is present *before* we actually try to open the external app
        markResume();

        if (openDelayRef.current) clearTimeout(openDelayRef.current);
        openDelayRef.current = window.setTimeout(() => {
            // Open the partner app directly
            const w = window.open(targetUrl, "_blank");
            if (!w) {
                // Fallback: same-tab navigation
                window.location.href = targetUrl;
            }
            afterNavigationAttempt?.();
            // We have navigated (new tab or this tab) — remember the URL for a no-delay re-open link
            setLastOpenedUrl(targetUrl);
            // Only clear the opening delay visual; "Verifying..." is controlled by isVerifying
            setOpeningSoon(false);
            openDelayRef.current = null;

            // Backstop: this timeout won't run while hidden,
            // but will fire immediately when the page becomes active again.
            window.setTimeout(() => {
                resumePolling();
            }, 0);
        }, delayMs);
    };

    // If integer part is 0 => show ".xx" (two decimals). Else 0-2 decimals, dot as decimal separator.
    const formatAmount = (n: number) => {
        if (!Number.isFinite(n)) return String(n);
        if (Math.abs(n) < 1) {
            // includes sign if any, then drop the leading 0 before the dot
            const s = n.toFixed(2); // e.g. "0.12" or "-0.12"
            return s.replace(/^-?0(?=\.)/, n < 0 ? "-" : "");
        }
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(n);
    };

    const formatReward = (r?: AnyReward | null) => {
        if (!r) return "—";
        const unit = r.reward?.unit ?? "";
        return `${formatAmount(r.reward.amount)} ${unit}`;
    };

    const primaryAndNext = useMemo(() => {
        if (!task)
            return {
                primary: null as AnyReward | null,
                next: null as AnyReward | null,
            };

        // Handle rewards structure - could be array or object with items
        let rewards: AnyReward[] = [];
        if (task.raw?.rewards) {
            if (Array.isArray(task.raw.rewards)) {
                rewards = task.raw.rewards as AnyReward[];
            } else if (
                (task.raw.rewards as any)?.items &&
                Array.isArray((task.raw.rewards as any).items)
            ) {
                rewards = (task.raw.rewards as any).items as AnyReward[];
            }
        }

        // 1) primary from array if available
        let primary = rewards.find((r) => r.isPrimaryReward) || null;

        // 2) fallback to task.primary_reward_*
        if (
            !primary &&
            task.raw?.primary_reward_amount != null &&
            task.raw?.primary_reward_unit
        ) {
            primary = {
                isPrimaryReward: true,
                type: "CRYPTO",
                reward: {
                    unit: String(task.raw.primary_reward_unit),
                    amount: Number(task.raw.primary_reward_amount),
                },
            };
        }

        // 3) "next" = first non-primary *after* primary in array order,
        //     else the first non-primary anywhere
        let next: AnyReward | null = null;
        if (rewards.length) {
            const pIdx = rewards.findIndex((r) => r.isPrimaryReward);
            if (pIdx >= 0) {
                next =
                    rewards.slice(pIdx + 1).find((r) => !r.isPrimaryReward) ||
                    rewards.find((r) => !r.isPrimaryReward) ||
                    null;
            } else {
                next = rewards.find((r) => !r.isPrimaryReward) || null;
            }
        }

        return { primary, next };
    }, [task]);

    const taskDescription = useMemo(() => {
        if (!task) return "";

        return (
            (typeof task.raw?.desc === "object" && task.raw?.desc?.[lang]) ??
            (typeof task.raw?.desc === "object" && task.raw?.desc?.en) ??
            (typeof task.raw?.desc === "string" ? task.raw?.desc : "") ??
            ""
        );
    }, [lang, task]);

    const handleDescScroll = useCallback(() => {
        const el = descRef.current;
        if (!el) return;

        const { scrollTop, scrollHeight, clientHeight } = el;
        const maxScroll = Math.max(scrollHeight - clientHeight, 1);
        const FADE_PX = 10;

        setDescTopFade(Math.min(scrollTop / FADE_PX, 1));
        setDescBottomFade(Math.min((maxScroll - scrollTop) / FADE_PX, 1));
    }, []);

    useEffect(() => {
        const el = descRef.current;
        if (!el) return;

        requestAnimationFrame(() => handleDescScroll());
        el.addEventListener("scroll", handleDescScroll, { passive: true });

        return () => el.removeEventListener("scroll", handleDescScroll);
    }, [handleDescScroll, taskDescription]);

    // ----- availability & enrollment helpers
    const isCheckInCampaign = task?.productType === "PARTNER_CAMPAIGN_CHI";
    const tz =
        (tc?.timezone && tc.timezone.length > 0 ? tc.timezone : undefined) ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;

    const sameLocalDay = (a: number, b: number, timeZone?: string) => {
        const fmt = (x: number) =>
            new Date(x).toLocaleDateString("en-CA", {
                timeZone,
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            });
        return fmt(a) === fmt(b);
    };

    // ---- helpers to compute "lives left" using the same TZ-stable logic as the grid
    const DAY_MS = 24 * 60 * 60 * 1000;
    const ymdParts = (ts: number, tzStr: string) => {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: tzStr,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(new Date(ts));
        const map: Record<string, string> = {};
        for (const p of parts) map[p.type] = p.value;
        return {
            y: Number(map.year),
            m: Number(map.month),
            d: Number(map.day),
        };
    };
    const serialDay = (ts: number, tzStr: string) => {
        const { y, m, d } = ymdParts(ts, tzStr);
        return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);
    };

    const sourceDays = localDaysCheckedIn ?? tc?.days_checked_in;
    const hasCheckedInToday = useMemo(() => {
        if (!isCheckInCampaign || !sourceDays?.length) return false;
        const now = Date.now();
        return sourceDays.some((d: number) => sameLocalDay(d, now, tz));
    }, [isCheckInCampaign, sourceDays, tz]);

    // From server: authoritative failure and absences remaining
    const hasFailed = !!task?.relation?.hasFailed;
    const livesLeft = task?.relation?.absencesLeft;

    const toIso = (v: unknown): string | undefined => {
        if (typeof v === "number") return new Date(v).toISOString();
        if (typeof v === "string") return v; // assume server already sent ISO
        return undefined;
    };

    const hasEverStarted =
        !!tc?.started_at || (tc?.days_checked_in?.length ?? 0) > 0;
    const enrolled =
        isCheckInCampaign &&
        ((!!tc &&
            (tc.status === "IN_PROGRESS" ||
                (tc as any).status === "STARTED")) ||
            hasEverStarted ||
            locallyStarted);

    const hasFinished =
        (localCompleted ?? false) ||
        (isCheckInCampaign &&
            !!tc &&
            (tc.status === "COMPLETED" || tc.status === "FAILED")) ||
        !!tc?.completedAt;

    // If the server says the user hasn't started, we should treat the server as authoritative
    const serverSaysNotStarted = useMemo(() => {
        if (!isCheckInCampaign || !tc) return false;
        const noStart = !tc.started_at;
        const noDays = (tc.days_checked_in?.length ?? 0) === 0;
        const st = tc.status as any;
        const notInProgress = st !== "IN_PROGRESS" && st !== "STARTED";
        // Only consider "not started" if we ourselves haven't just locally marked it as started
        return noStart && noDays && notInProgress && !locallyStarted;
    }, [
        isCheckInCampaign,
        tc?.started_at,
        tc?.days_checked_in,
        tc?.status,
        locallyStarted,
    ]);

    const sessionActive = useMemo(() => {
        const ttlOk = (progress?.expiresAt ?? 0) > Date.now();
        return !!(
            startedVerification &&
            progress?.initiated &&
            progress?.code &&
            ttlOk &&
            !hasFinished &&
            !hasCheckedInToday
        );
    }, [
        startedVerification,
        progress?.initiated,
        progress?.code,
        progress?.expiresAt,
        hasFinished,
        hasCheckedInToday,
    ]);

    // Use this to drive the UI (sticky + derived)
    const verifyingUI = isVerifying || sessionActive;

    const showReopenLink =
        !!lastOpenedUrl && !(enrolled && hasCheckedInToday) && !verifyingUI; // hide if "Come back tomorrow!" state
    const noSpots =
        typeof task?.spotsLeft === "number" ? task.spotsLeft <= 0 : false;
    // Treat legacy/LIVE status as available too (pre-migration behavior)
    const isOpenStatus =
        task?.status === "AVAILABLE" || task?.status === "LIVE";
    // Available for THIS user if task is open and either has spots OR the user is already enrolled
    const isAvailableForUser =
        isOpenStatus && !hasFinished && (enrolled || !noSpots);

    // Updated useEffect hooks that depend on hasCheckedInToday and hasFinished
    // Resume progress (and polling) if a (re)load finds a still-valid code in localStorage
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            // If server says not started, purge any local residue immediately
            if (serverSaysNotStarted) {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }
            const saved = JSON.parse(raw) as {
                code: string;
                expiresAt: number;
                status?: "PENDING" | "ACKED" | "APPLIED";
                initiated?: boolean;
            };
            if (!saved?.expiresAt || saved.expiresAt <= Date.now()) {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }
            // If we already checked in today or the campaign is finished,
            // do NOT resume an old PENDING session. Drop it.
            if (hasCheckedInToday || hasFinished) {
                localStorage.removeItem(STORAGE_KEY);
                return;
            }
            // Only auto-resume if the user had initiated verification previously
            if (saved.status === "APPLIED") {
                // If previous session already reached APPLIED, reflect it immediately
                applySuccessfulCheckIn();
                return;
            }
            if (saved.initiated ?? true) {
                // treat missing flag as initiated
                setProgress({
                    ...saved,
                    status: saved.status ?? "PENDING",
                    initiated: true,
                });
                setStartedVerification(true);
                setIsVerifying(true);
                beginTick();
                startPollingStatus(saved.code, saved.expiresAt);
            } else {
                // keep the prefetched code in memory if you want (doesn't trigger UI)
                setProgress({
                    ...saved,
                    status: saved.status ?? "PENDING",
                    initiated: false,
                });
            }
        } catch (error) {
            throwUnhandledError(error, "Unable to restore check-in progress");
        }
    }, [STORAGE_KEY, hasCheckedInToday, hasFinished, serverSaysNotStarted]);

    // If user comes back from the mini-app (same tab or another), kick polling immediately.
    useEffect(() => {
        const onFocus = () => {
            if (startedVerification) resumePolling();
        };
        const onVisibility = () => {
            if (document.visibilityState === "visible" && startedVerification) {
                resumePolling();
            } else if (document.visibilityState === "hidden") {
                // proactively clear timers before the page is backgrounded
                stopPolling();
            }
        };
        const onPageShow = () => {
            // Safari/WKWebView bf-cache returns fire this reliably
            if (startedVerification) resumePolling();
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pageshow", onPageShow);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pageshow", onPageShow);
        };
    }, [
        startedVerification,
        progress?.code,
        progress?.expiresAt,
        hasFinished,
        hasCheckedInToday,
    ]);

    // Sticky: once verifying starts, keep it true until we explicitly end it
    useEffect(() => {
        if (startedVerification && !hasCheckedInToday && !hasFinished) {
            setIsVerifying(true);
        }
    }, [startedVerification, hasCheckedInToday, hasFinished]);

    // Start polling as soon as the launcher tab writes the code
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key !== STORAGE_KEY || !e.newValue) return;
            try {
                // If server says not started, we delete and stop here
                if (serverSaysNotStarted) {
                    try {
                        localStorage.removeItem(STORAGE_KEY);
                    } catch (error) {
                        throwUnhandledError(
                            error,
                            "Unable to clear server-reset check-in progress",
                        );
                    }
                    return;
                }
                const saved = JSON.parse(e.newValue);
                if (saved?.status === "APPLIED") {
                    applySuccessfulCheckIn();
                    return;
                }
                if (saved?.expiresAt > Date.now() && saved.code) {
                    if (hasCheckedInToday || hasFinished) {
                        try {
                            localStorage.removeItem(STORAGE_KEY);
                        } catch (error) {
                            throwUnhandledError(
                                error,
                                "Unable to clear completed check-in progress",
                            );
                        }
                        return;
                    }
                    if (saved.initiated) {
                        setProgress({
                            ...saved,
                            status: "PENDING",
                            initiated: true,
                        });
                        setStartedVerification(true);
                        setIsVerifying(true);
                        beginTick();
                        startPollingStatus(saved.code, saved.expiresAt);
                    } else {
                        setProgress({
                            ...saved,
                            status: "PENDING",
                            initiated: false,
                        });
                    }
                }
            } catch (error) {
                throwUnhandledError(
                    error,
                    "Unable to process check-in storage event",
                );
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [STORAGE_KEY, hasCheckedInToday, hasFinished, serverSaysNotStarted]);

    // Cleanup any lingering progress once state flips (e.g., after the first render when task arrives)
    useEffect(() => {
        if ((hasCheckedInToday || hasFinished) && progress) {
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch (error) {
                throwUnhandledError(
                    error,
                    "Unable to clear finished check-in progress",
                );
            }
            setIsVerifying(false);
            setStartedVerification(false);
            setProgress(null);
            stopPolling();
            if (tickRef.current) {
                clearInterval(tickRef.current);
                tickRef.current = null;
            }
        }
    }, [hasCheckedInToday, hasFinished]);

    // NEW: If the server indicates "not started", nuke any local progress and timers (server authoritative)
    useEffect(() => {
        if (!serverSaysNotStarted) return;
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            throwUnhandledError(
                error,
                "Unable to clear authoritative check-in progress",
            );
        }
        setIsVerifying(false);
        setStartedVerification(false);
        setProgress(null);
        stopPolling();
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, [serverSaysNotStarted, STORAGE_KEY]);

    // Short-lived watchdog while "verifying"
    useEffect(() => {
        if (!startedVerification || hasFinished || hasCheckedInToday) return;
        if (!progress?.code) return;
        if (pollRef.current) return;

        // Try immediately if visible
        if (document.visibilityState === "visible") {
            resumePolling();
        }

        // Backstop timer: will run as soon as the page is active again
        const backstop = window.setTimeout(() => {
            if (!pollRef.current && document.visibilityState === "visible") {
                resumePolling();
            }
        }, 250);

        // Any tap/click also wakes it
        const onAnyUserAction = () => {
            if (!pollRef.current) resumePolling();
        };
        window.addEventListener("pointerdown", onAnyUserAction, { once: true });

        return () => {
            clearTimeout(backstop);
            window.removeEventListener("pointerdown", onAnyUserAction);
        };
    }, [startedVerification, progress?.code, hasFinished, hasCheckedInToday]);

    const startStreak = async () => {
        if (!task) return;
        try {
            setIsSubmitting(true);
            const token = await WorldAuthClient.getAccessTokenSilently();
            tokenRef.current = token.accessToken;
            const clientVersion =
                (import.meta as any)?.env?.VITE_APP_VERSION ||
                (window as any)?.APP_VERSION ||
                "web";
            const body: z.infer<typeof ApiChiStartReqSchema> = {
                // clientVersion,
                // partnerCampaignId: task.itemId,
                // version: (task as any)?.response_version ?? 1,
                // renderedSpotsLeft:
                //     typeof task.spotsLeft === "number" ? task.spotsLeft : -1,
                // timezone: tz,
                // taskToken: task.listingToken,

                partnerCampaignId: task.itemId,
                taskToken: task.listingToken ?? "",
                version: (task as any)?.response_version ?? "1",
                clientVersion,
                timezone: tz,
                renderedSpotsLeft:
                    typeof task.spotsLeft === "number" ? task.spotsLeft : -1,
            };
            const res = await fetch(API_TASK_START, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token.accessToken}`,
                },
                body: JSON.stringify(body),
            });
            const json = await res.json().catch(() => ({}));
            const parsedStart = ApiChiStartResSchema.safeParse(json);
            if (
                !res.ok ||
                !parsedStart.success ||
                parsedStart.data.status !== "OK"
            ) {
                const errMsg =
                    (parsedStart.success
                        ? undefined
                        : "Invalid server payload") ||
                    json?.error ||
                    json?.message ||
                    `Failed to start (HTTP ${res.status}).`;
                throw new Error(errMsg);
            }

            // If the server returned a TaskCompletion, persist it into the slice
            const serverTC = parsedStart.data.participation;
            if (serverTC) {
                // Adapt serverTC (snake_case likely) onto our feed item relation
                const serverDays =
                    (serverTC as any)?.days_checked_in ??
                    (serverTC as any)?.daysCheckedIn ??
                    [];
                setFeedSlice({
                    ...feedState,
                    items: feedState.items.map((it) => {
                        if (
                            it.raw.id !== task.raw.id ||
                            it.productType !== "PARTNER_CAMPAIGN_CHI"
                        )
                            return it;
                        const rel = it.relation as ChiItem["relation"];
                        return {
                            ...it,
                            relation: {
                                ...rel,
                                status:
                                    (serverTC as any)?.status ?? "IN_PROGRESS",
                                // startedAt:
                                //     (serverTC as any)?.started_at ??
                                //     (serverTC as any)?.startedAt ??
                                //     rel?.startedAt,

                                startedAt: toIso(
                                    (serverTC as any)?.started_at ??
                                        (serverTC as any)?.startedAt ??
                                        rel?.startedAt,
                                ),
                                updatedAt: toIso(
                                    (serverTC as any)?.updated_at ??
                                        (serverTC as any)?.updatedAt ??
                                        rel?.updatedAt,
                                ),
                                completedAt: toIso(
                                    (serverTC as any)?.completed_at ??
                                        (serverTC as any)?.completedAt ??
                                        rel?.completedAt,
                                ),
                                canStart: true,
                                canResume: true,
                                requiredDays:
                                    (serverTC as any)?.required_days ??
                                    rel?.requiredDays,
                                toleranceDays:
                                    (serverTC as any)?.tolerance_days ??
                                    rel?.toleranceDays,
                                daysCheckedIn: Array.isArray(serverDays)
                                    ? serverDays.map((n: any) => Number(n))
                                    : rel?.daysCheckedIn,
                            },
                        } as ChiItem;
                    }),
                });
                // prime local mirrors for instant UI
                setLocalDaysCheckedIn(
                    Array.isArray(serverDays)
                        ? serverDays.map((n: any) => Number(n))
                        : [],
                );
                setLocalCompleted(
                    (serverTC as any)?.status === "COMPLETED" ||
                        (serverTC as any)?.status === "FAILED",
                );
            }

            // Mark as started to allow opening with a code
            setLocallyStarted(true);
            // (Remove the old guarded slice update that early-returned when tc was missing)

            // Get a short code immediately and *launch with code* + start polling.
            let codeInfo: { code: string; expiresAt: number } | null = null;
            try {
                codeInfo = await withRetries(
                    async () => {
                        const r = await prefetchProgressCode();
                        if (!r) throw new Error("code-not-ready");
                        return r;
                    },
                    4,
                    400,
                );
            } catch {
                codeInfo = null;
            }

            if (codeInfo?.code) {
                const { code, expiresAt } = codeInfo;
                const targetURL = buildPartnerUrl(partnerAppId, code);
                if (shouldOptimisticallyApplyCheckIn) {
                    openViaHandshake(targetURL, 1750, () =>
                        startVerificationAfterNavigation(code, expiresAt),
                    );
                } else {
                    markProgressInitiated(code, expiresAt);
                    openViaHandshake(targetURL, 1750);
                }
            } else {
                // If code couldn't be fetched quickly, fall back to raw URL (user can tap again)
                const rawUrl =
                    (task.raw?.check_in_campaign as any)?.app_url ??
                    buildPartnerBaseUrl(partnerAppId);
                openViaHandshake(rawUrl, 1750);
            }
        } catch (e) {
            console.error("Start streak failed:", e);
            alert(
                e instanceof Error
                    ? e.message
                    : "Could not start this campaign. Please try again.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // ---------- Progress Code Flow ----------
    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    // When a check-in succeeds, update local mirrors AND the feed slice (authoritative for UI)
    const applySuccessfulCheckIn = (opts?: { completed?: boolean }) => {
        const nowMs = Date.now();
        // local mirrors
        setLocalDaysCheckedIn((prev) => {
            const base = prev ?? tc?.days_checked_in ?? [];
            const hasToday = base.some((d: number) =>
                sameLocalDay(d, nowMs, tz),
            );
            return hasToday ? base.slice() : [...base, nowMs];
        });
        if (typeof opts?.completed === "boolean") {
            setLocalCompleted(opts.completed);
        }
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            throwUnhandledError(
                error,
                "Unable to clear successful check-in progress",
            );
        }
        // slice: append "today" (day-deduped), ensure status/startedAt
        setFeedSlice({
            ...feedState,
            items: feedState.items.map((it) => {
                if (
                    it.raw.id !== task?.raw.id ||
                    it.productType !== "PARTNER_CAMPAIGN_CHI"
                )
                    return it;
                const rel = it.relation as ChiItem["relation"];
                const existing = rel?.daysCheckedIn ?? [];
                const alreadyToday = existing.some((d: number) =>
                    sameLocalDay(d, nowMs, tz),
                );
                const updatedDays = alreadyToday
                    ? existing.slice()
                    : [...existing, nowMs];
                return {
                    ...it,
                    relation: {
                        ...rel,
                        daysCheckedIn: updatedDays,
                        status:
                            rel?.status === "COMPLETED" ||
                            rel?.status === "FAILED"
                                ? rel?.status
                                : ("IN_PROGRESS" as const),
                        startedAt:
                            rel?.startedAt ?? new Date(nowMs).toISOString(),
                        updatedAt: new Date(nowMs).toISOString(),
                    },
                } as ChiItem;
            }),
        });
        // end any verification UI/timers
        setIsVerifying(false);
        setStartedVerification(false);
        setProgress(null);
        stopPolling();
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    const remainingSec = () => {
        if (!progress?.expiresAt) return 0;
        return Math.max(
            0,
            Math.floor((progress.expiresAt - Date.now()) / 1000),
        );
    };

    const beginTick = () => {
        if (tickRef.current) clearInterval(tickRef.current);
        tickRef.current = window.setInterval(() => {
            forceTick((x) => x + 1);
            if (remainingSec() <= 0) {
                setProgress((p) => (p ? { ...p, status: "EXPIRED" } : p));
                stopPolling();
            }
        }, 1000);
    };

    const markProgressInitiated = (code: string, expiresAt?: number) => {
        setStartedVerification(true);
        setProgress((p) => ({
            code,
            expiresAt: expiresAt ?? p?.expiresAt,
            status:
                p?.status === "ACKED" || p?.status === "APPLIED"
                    ? p.status
                    : "PENDING",
            initiated: true,
        }));
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    ...saved,
                    code,
                    expiresAt: expiresAt ?? saved?.expiresAt,
                    status:
                        saved?.status === "ACKED" || saved?.status === "APPLIED"
                            ? saved.status
                            : "PENDING",
                    initiated: true,
                }),
            );
        } catch (error) {
            throwUnhandledError(error, "Unable to mark check-in as initiated");
        }
        setIsVerifying(true);
    };

    const startVerificationAfterNavigation = (
        code: string,
        expiresAt?: number,
    ) => {
        markProgressInitiated(code, expiresAt);
        beginTick();
        startPollingStatus(code, expiresAt);
    };

    // simple retry helper with exponential backoff
    const withRetries = async <T,>(
        fn: () => Promise<T>,
        attempts = 3,
        baseDelayMs = 900,
    ): Promise<T> => {
        let lastErr: unknown;
        for (let i = 0; i < attempts; i++) {
            try {
                return await fn();
            } catch (e) {
                lastErr = e;
                if (i < attempts - 1) {
                    const delay = baseDelayMs * Math.pow(2, i);
                    await new Promise((r) => setTimeout(r, delay));
                }
            }
        }
        throw lastErr;
    };

    // ---- Prefetch a code (init) so we don't need to await on click
    const prefetchProgressCode = async (): Promise<{
        code: string;
        expiresAt: number;
    } | null> => {
        if (!task || prefetchingRef.current) return null;
        prefetchingRef.current = true;
        try {
            const token = await WorldAuthClient.getAccessTokenSilently();
            tokenRef.current = token.accessToken;
            const res = await fetch(API_TASK_PROGRESS_INIT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token.accessToken}`,
                },
                body: JSON.stringify({ taskId: task.itemId }),
            });
            const json = await res.json().catch(() => ({}));
            const parsed = ApiChiProgressInitResSchema.safeParse(json);
            if (!res.ok || !parsed.success || parsed.data.status !== "OK") {
                return null;
            }
            const { code, expiresAt } = parsed.data;
            const next = {
                code,
                expiresAt,
                status: "PENDING" as const,
                initiated: false,
            };
            setProgress(next);
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            } catch (error) {
                throwUnhandledError(
                    error,
                    "Unable to persist prefetched check-in code",
                );
            }
            return { code, expiresAt };
        } catch (error) {
            throwUnhandledError(error, "Unable to prefetch check-in code");
            return null;
        } finally {
            prefetchingRef.current = false;
        }
    };

    // When user is eligible to open (enrolled & not checked-in today), prefetch on-load
    // and refresh every 4m50s to keep a fresh short code available.
    useEffect(() => {
        const shouldPrefetch =
            !!task &&
            isCheckInCampaign &&
            enrolled &&
            !hasCheckedInToday &&
            isAvailableForUser;
        if (!shouldPrefetch) {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
            return;
        }

        const now = Date.now();
        const hasValid =
            progress?.code &&
            progress?.expiresAt &&
            progress.expiresAt > now + 5_000;
        if (!hasValid) {
            void prefetchProgressCode();
        }

        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
        }
        // 4m50s = 290000 ms (Redis TTL is 300s)
        refreshIntervalRef.current = window.setInterval(() => {
            void prefetchProgressCode();
        }, 290_000);

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [
        task?.raw.id,
        isCheckInCampaign,
        enrolled,
        hasCheckedInToday,
        isAvailableForUser,
        // do not include `progress` so we don't reset interval on every update
    ]);

    const startPollingStatus = (code: string, expiresAt?: number) => {
        // ensure we never keep a frozen/stale timer
        if (pollRef.current) clearInterval(pollRef.current);

        const poll = async () => {
            try {
                const accessToken = await getAccessToken();
                const res = await fetch(API_TASK_PROGRESS_STATUS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({ code }),
                });
                if (res.status === 401) tokenRef.current = null;
                const json = await res.json().catch(() => ({}));
                const parsed = ApiChiProgressStatusResSchema.safeParse(json);
                if (!parsed.success || parsed.data.status !== "OK") {
                    return; // keep polling on transient hiccup
                }
                const ps = parsed.data.progressStatus;
                setProgress((p) => {
                    const next = p
                        ? { ...p, status: ps }
                        : { code, status: ps, expiresAt }; // preserve TTL on first write
                    try {
                        if (next.code && next.expiresAt) {
                            localStorage.setItem(
                                STORAGE_KEY,
                                JSON.stringify(next),
                            );
                        }
                    } catch (error) {
                        throwUnhandledError(
                            error,
                            "Unable to persist check-in status",
                        );
                    }
                    return next;
                });

                // Terminal states cleanups remain
                if (ps === "EXPIRED") {
                    try {
                        localStorage.removeItem(STORAGE_KEY);
                    } catch (error) {
                        throwUnhandledError(
                            error,
                            "Unable to clear expired check-in progress",
                        );
                    }
                    stopPolling();
                    if (refreshIntervalRef.current) {
                        clearInterval(refreshIntervalRef.current);
                        refreshIntervalRef.current = null;
                    }
                }
            } catch (error) {
                throwUnhandledError(
                    error,
                    "Unable to poll check-in progress status",
                );
                // Continue polling on transient hiccups.
            }
        };

        // lead with an immediate request so UI updates without waiting a tick
        void poll();
        pollRef.current = window.setInterval(poll, 1000);
    };

    // React whenever progress transitions to APPLIED (even if it came from localStorage / storage event)
    useEffect(() => {
        if (
            progress?.status === "APPLIED" &&
            !hasCheckedInToday &&
            !hasFinished
        ) {
            // We don't know `completed` here unless it came from the latest poll.
            // This still safely marks "today" and updates slice; claiming flow will set COMPLETED later.
            applySuccessfulCheckIn();
        }
    }, [progress?.status, hasCheckedInToday, hasFinished]);

    // ---- Claim reward(s) from /chi/complete and execute on-chain claims
    const claimReward = async () => {
        if (!task) return;
        setIsSubmitting(true);
        try {
            const token = await WorldAuthClient.getAccessTokenSilently();
            const res = await fetch(API_TASK_COMPLETE, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token.accessToken}`,
                },
                body: JSON.stringify({ taskId: task.itemId }),
            });
            const json = await res.json().catch(() => ({}));

            const parsed = ApiChiCompleteResSchema.safeParse(json);
            if (!res.ok || !parsed.success || parsed.data.status !== "OK") {
                const errMsg =
                    (parsed.success ? undefined : "Invalid server payload") ||
                    json?.error ||
                    json?.message ||
                    `Failed to claim (HTTP ${res.status}).`;
                throw new Error(errMsg);
            }

            // Split rewards: on-chain (CRYPTO) vs off-chain (PROGRESSION)
            const cryptoRewards = parsed.data.rewards.filter(
                (r) => r.data.type === "CRYPTO",
            );

            // If we have vouchers, try to claim them in a single batch first
            if (cryptoRewards.length > 0) {
                const txs = cryptoRewards.map((reward) => {
                    const d =
                        reward.data.type === "CRYPTO" ? reward.data.data : null;
                    if (!d) throw new Error("Unexpected reward payload");
                    return {
                        address: VOUCHER_FAUCET_ADDRESS,
                        abi: VoucherFaucetABI.abi,
                        functionName: "claim",
                        args: [d.unitContract, d.amount, d.nonce, d.signature],
                    } as const;
                });

                // 1) Batch attempt
                let batchSucceeded = false;
                try {
                    const batchRes = await withRetries(
                        () => claim(txs as any),
                        2, // 2 tries batch
                        900,
                    );
                    if ((batchRes as any)?.status !== "error") {
                        batchSucceeded = true;
                    }
                } catch {
                    // fall through to per-voucher retries
                }

                // 2) If batch failed, try each voucher sequentially with retries
                if (!batchSucceeded) {
                    for (const tx of txs) {
                        await withRetries(
                            async () => {
                                const r = await claim([tx] as any);
                                if ((r as any)?.status === "error") {
                                    throw new Error("voucher-claim-failed");
                                }
                                return r;
                            },
                            3, // attempts per voucher
                            1000,
                        );
                    }
                }
            }

            // SET TO COMPLETED
            setFeedSlice({
                ...feedState,
                items: feedState.items.map((it) => {
                    if (
                        it.raw.id !== task.raw.id ||
                        it.productType !== "PARTNER_CAMPAIGN_CHI"
                    )
                        return it;
                    const rel = it.relation as ChiItem["relation"];
                    return {
                        ...it,
                        relation: {
                            ...rel,
                            status: "COMPLETED",
                            completedAt: new Date().toISOString(),
                        },
                    } as ChiItem;
                }),
            });

            setLocalCompleted(true);
        } catch (e) {
            console.error("Claim failed:", e);
            alert(
                e instanceof Error
                    ? e.message
                    : t(
                          "TaskOverViewCheckInCampaign.couldNotClaimRewardPleaseTryAgain",
                      ),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClick = async () => {
        if (btnDisabled) return;
        if (isCheckInCampaign) {
            // Claim branch first (highest priority)
            if (canClaim) {
                await claimReward();
                return;
            }
            if (enrolled) {
                // If user hasn't checked in today yet -> open partner with prefetched code and start polling
                if (!hasCheckedInToday) {
                    const now = Date.now();
                    if (
                        progress?.code &&
                        progress?.expiresAt &&
                        progress.expiresAt > now + 5_000
                    ) {
                        const { code, expiresAt } = progress;
                        const targetURL = buildPartnerUrl(partnerAppId, code);
                        console.log("targetURL", targetURL);
                        // mark resume in case returning triggers a reload
                        markResume();
                        if (shouldOptimisticallyApplyCheckIn) {
                            openViaHandshake(targetURL, 1750, () =>
                                startVerificationAfterNavigation(
                                    code,
                                    expiresAt,
                                ),
                            );
                        } else {
                            markProgressInitiated(code, expiresAt);
                            openViaHandshake(targetURL, 1750);
                        }
                    } else {
                        // Extremely rare fallback: code not ready. Kick off a prefetch
                        // and ask the user to try again shortly.
                        void prefetchProgressCode();
                        alert(
                            t(
                                "TaskOverViewCheckInCampaign.preparingYourCheckInCodePleaseTryAgainInAMoment",
                            ),
                        );
                    }
                    return;
                }

                // Already checked in today -> just open the app normally (no init/poll needed)
                const rawUrl =
                    (task.raw?.check_in_campaign as any)?.app_url ??
                    buildPartnerBaseUrl(partnerAppId);
                markResume();
                openViaHandshake(rawUrl, 1750);
                return;
            }
            // Starting the streak will also navigate; mark resume beforehand
            markResume();
            await startStreak();
            return;
        }
        // fallback for non-check-in tasks (if ever shown in this view)
        console.warn("Non check-in task open not implemented in this view.");
    };

    const rewardUnit = (r?: AnyReward | null) =>
        r?.reward?.unit?.toUpperCase?.() || "";

    const unitClass = (r?: AnyReward | null) => {
        const u = rewardUnit(r);
        if (u === "WLD") return "tovcic__content__rewards__card--wld";
        if (u === "GOLD") return "tovcic__content__rewards__card--gold";
        if (u === "USDC") return "tovcic__content__rewards__card--usdc";
        if (u === "ORO") return "tovcic__content__rewards__card--oro";
        return "";
    };

    // ---- completion threshold: user can claim once enough unique check-ins reached
    // const canClaim = useMemo(() => {
    //     if (!isCheckInCampaign || !tc?.started_at) return false;
    //     console.log(
    //         "canClaim",
    //         task?.raw?.check_in_campaign?.required_days,
    //         tc?.day_amount,
    //         tc?.day_tolerance,
    //         task?.raw?.check_in_campaign?.tolerance_days
    //     );
    //     const totalDays =
    //         task?.raw?.check_in_campaign?.required_days ??
    //         tc?.day_amount ??
    //         task?.relation?.requiredDays ??
    //         7;
    //     const allowed =
    //         tc?.day_tolerance ??
    //         task?.raw?.check_in_campaign?.tolerance_days ??
    //         task?.relation?.toleranceDays ??
    //         0;
    //     const requiredChecks = Math.max(0, totalDays - allowed);

    //     const base = serialDay(tc.started_at, tz);
    //     const uniq = new Set<number>();
    //     for (const d of (sourceDays ?? []) as number[]) {
    //         const idx = serialDay(d, tz) - base;
    //         if (idx >= 0 && idx < totalDays) uniq.add(idx);
    //     }
    //     return uniq.size >= requiredChecks && !hasFailed;
    // }, [
    //     isCheckInCampaign,
    //     tc?.started_at,
    //     tc?.day_tolerance,
    //     task?.raw?.check_in_campaign?.tolerance_days,
    //     task?.raw?.check_in_campaign?.required_days,
    //     tc?.day_amount,
    //     tz,
    //     sourceDays,
    //     hasFailed,
    // ]);

    // ---- completion threshold: user can claim only on the last day, and if not failed
    const canClaim = useMemo(() => {
        if (!isCheckInCampaign || !tc?.started_at) return false;

        const totalDays =
            task?.raw?.check_in_campaign?.required_days ??
            tc?.day_amount ??
            task?.relation?.requiredDays ??
            7;

        const allowed =
            tc?.day_tolerance ??
            task?.raw?.check_in_campaign?.tolerance_days ??
            task?.relation?.toleranceDays ??
            0;

        const requiredChecks = Math.max(0, totalDays - allowed);

        const base = serialDay(tc.started_at, tz);

        // NEW: Only allow on the last day of the window
        const nowIdx = serialDay(Date.now(), tz) - base;
        const isLastDay = nowIdx === totalDays - 1;

        // Unique check-ins within the window
        const uniq = new Set<number>();
        for (const d of (sourceDays ?? []) as number[]) {
            const idx = serialDay(d, tz) - base;
            if (idx >= 0 && idx < totalDays) uniq.add(idx);
        }

        // Must be last day AND meet tolerance rule AND not failed already
        return isLastDay && uniq.size >= requiredChecks && !hasFailed;
    }, [
        isCheckInCampaign,
        tc?.started_at,
        task?.raw?.check_in_campaign?.required_days,
        tc?.day_amount,
        tc?.day_tolerance,
        task?.raw?.check_in_campaign?.tolerance_days,
        task?.relation?.toleranceDays,
        tz,
        sourceDays,
        hasFailed,
    ]);

    let btnLabel: string;
    let btnDisabled = false;
    let stateClass = "";

    if (hasFailed) {
        btnLabel = t("TaskOverViewCheckInCampaign.tryAgainNextTime");
        btnDisabled = true;
        stateClass = "state-failed";
    } else if (verifyingUI) {
        btnLabel = t("TaskOverViewCheckInCampaign.checking");
        stateClass = "state-waiting";
    } else if (openingSoon) {
        // optional: show a brief "Opening..." while we delay the popup (non-verification paths)
        btnLabel = t("TaskOverViewCheckInCampaign.opening");
        stateClass = "state-open";
    } else if (hasFinished) {
        // NEW: already completed (either just now or before component mounted)
        btnLabel = t("TaskOverViewCheckInCampaign.rewardsClaimed");
        btnDisabled = true; // lock the button
        stateClass = "state-claimed"; // optional styling hook
    } else if (canClaim) {
        btnLabel = t("TaskOverViewCheckInCampaign.claimRewards");
        stateClass = "state-claim";
    } else if (!isAvailableForUser) {
        btnLabel = t("TaskOverViewCheckInCampaign.taskUnavailable");
        btnDisabled = true;
        stateClass = "state-unavailable";
    } else if (enrolled) {
        if (hasCheckedInToday) {
            btnLabel = t("TaskOverViewCheckInCampaign.comeBackTomorrow");
            btnDisabled = true;
            stateClass = "state-checked-today";
        } else {
            btnLabel = t("TaskOverViewCheckInCampaign.openAppX", {
                appName:
                    (typeof task?.raw?.partner_name === "object" &&
                        task?.raw?.partner_name?.[lang]) ??
                    (typeof task?.raw?.partner_name === "object" &&
                        task?.raw?.partner_name?.en) ??
                    (typeof task?.raw?.partner_name === "string"
                        ? task?.raw?.partner_name
                        : "") ??
                    "app",
            });
            stateClass = "state-open";
        }
    } else {
        if ((task.raw.check_in_campaign?.required_days ?? 0) > 1) {
            btnLabel = t("TaskOverViewCheckInCampaign.startStreak");
        } else {
            btnLabel = t("TaskOverViewCheckInCampaign.startTask");
        }
        stateClass = "state-start";
    }

    // also disable while in-flight
    btnDisabled = btnDisabled || isSubmitting || openingSoon || verifyingUI;

    // IMPORTANT: all hooks are declared above. It's now safe to bail out
    // without changing the hooks order between renders.
    if (!task) return null;

    // (Removed) DOM listener-based trigger; use openViaHandshake instead.
    const primaryRewardUnit = rewardUnit(primaryAndNext.primary);
    const nextRewardUnit = rewardUnit(primaryAndNext.next);

    return (
        <div className="tovcic">
            <div className="tovcic__header">
                <div className="tovcic__header__img-container">
                    <img
                        src={task.raw?.partner_logo_url ?? undefined}
                        alt=""
                        className="tovcic__header__img"
                    />
                </div>

                <button
                    className="tovcic__header__btn-container"
                    onClick={onClose}
                >
                    <div className="tovcic__header__btn">
                        <FontAwesomeIcon icon={faXmark} />
                    </div>
                </button>
            </div>

            <div className="tovcic__content">
                <div className="tovcic__content__title">
                    <h1 className="tovcic__content__title__title">
                        {(typeof task.raw?.title === "object" &&
                            task.raw?.title?.[lang]) ??
                            (typeof task.raw?.title === "object" &&
                                task.raw?.title?.en) ??
                            (typeof task.raw?.title === "string"
                                ? task.raw?.title
                                : "") ??
                            task.title ??
                            ""}
                    </h1>
                    <div className="tovcic__content__title__description-wrapper">
                        <p
                            ref={descRef}
                            className="tovcic__content__title__description"
                        >
                            {taskDescription}
                        </p>
                        {descTopFade > 0 && (
                            <div
                                className="tovcic__content__title__description-fade top"
                                style={{ opacity: descTopFade }}
                            />
                        )}
                        {descBottomFade > 0 && (
                            <div
                                className="tovcic__content__title__description-fade bottom"
                                style={{ opacity: descBottomFade }}
                            />
                        )}
                    </div>
                </div>

                <div className="tovcic__content__rewards">
                    <div
                        className={[
                            "tovcic__content__rewards__card",
                            "tovcic__content__rewards__card--primary",
                            // IS-SINGLE-REWARD
                            !primaryAndNext.next ? "single" : "",
                            unitClass(primaryAndNext.primary),
                        ].join(" ")}
                    >
                        {primaryRewardUnit === "WLD" && (
                            <>
                                <WldIcon className="tovcic__content__rewards__card__icon" />
                                <WldIcon className="tovcic__content__rewards__card__bg-icon" />
                            </>
                        )}
                        {primaryRewardUnit === "USDC" && (
                            <>
                                <UsdcIcon className="tovcic__content__rewards__card__icon" />
                                <UsdcIcon className="tovcic__content__rewards__card__bg-icon" />
                            </>
                        )}
                        {primaryRewardUnit === "GOLD" && (
                            <>
                                <GoldIcon className="tovcic__content__rewards__card__icon" />
                                <GoldIcon className="tovcic__content__rewards__card__bg-icon" />
                                <BgStar className="tovcic__content__rewards__card__bg-star pr n3" />
                                <BgStar className="tovcic__content__rewards__card__bg-star nx n4" />
                            </>
                        )}
                        {primaryRewardUnit === "ORO" && (
                            <>
                                <img
                                    src={OroLogoImg}
                                    alt=""
                                    className="tovcic__content__rewards__card__icon tovcic__content__rewards__card__icon--img"
                                />
                                <img
                                    src={OroLogoImg}
                                    alt=""
                                    className="tovcic__content__rewards__card__bg-icon tovcic__content__rewards__card__bg-icon--img"
                                />
                            </>
                        )}
                        <BgStar className="tovcic__content__rewards__card__bg-star pr n1" />
                        <BgStar className="tovcic__content__rewards__card__bg-star pr n2" />

                        <div className="tovcic__content__rewards__card__value">
                            {formatReward(primaryAndNext.primary)}
                        </div>
                    </div>

                    {primaryAndNext.next && (
                        <div className="tovcic__content__rewards__plus">
                            <FontAwesomeIcon icon={faPlus} />
                        </div>
                    )}

                    {primaryAndNext.next ? (
                        <div
                            className={[
                                "tovcic__content__rewards__card",
                                "tovcic__content__rewards__card--next",
                                unitClass(primaryAndNext.next),
                            ].join(" ")}
                        >
                            {nextRewardUnit === "GOLD" && (
                                <>
                                    <GoldIcon className="tovcic__content__rewards__card__icon" />
                                    <GoldIcon className="tovcic__content__rewards__card__bg-icon" />
                                </>
                            )}
                            {nextRewardUnit === "USDC" && (
                                <>
                                    <UsdcIcon className="tovcic__content__rewards__card__icon" />
                                    <UsdcIcon className="tovcic__content__rewards__card__bg-icon" />
                                </>
                            )}
                            {nextRewardUnit === "WLD" && (
                                <>
                                    <WldIcon className="tovcic__content__rewards__card__icon" />
                                    <WldIcon className="tovcic__content__rewards__card__bg-icon" />
                                </>
                            )}
                            {nextRewardUnit === "ORO" && (
                                <>
                                    <img
                                        src={OroLogoImg}
                                        alt=""
                                        className="tovcic__content__rewards__card__icon tovcic__content__rewards__card__icon--img"
                                    />
                                    <img
                                        src={OroLogoImg}
                                        alt=""
                                        className="tovcic__content__rewards__card__bg-icon tovcic__content__rewards__card__bg-icon--img"
                                    />
                                </>
                            )}
                            <BgStar className="tovcic__content__rewards__card__bg-star nx n1" />
                            <BgStar className="tovcic__content__rewards__card__bg-star nx n2" />

                            <div className="tovcic__content__rewards__card__value">
                                {formatReward(primaryAndNext.next)}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="tovcic__content__hdiv" />

                {task?.raw?.check_in_campaign?.required_days &&
                task?.raw?.check_in_campaign?.required_days > 1 ? (
                    <div
                        className={`tovcic__content__day-grid ${
                            showReopenLink
                                ? "tovcic__content__day-grid--with-reopen"
                                : ""
                        }`}
                    >
                        <CheckInDayGrid
                            task={task}
                            createdAt={
                                tc?.started_at ??
                                (localDaysCheckedIn?.length
                                    ? Math.min(...localDaysCheckedIn)
                                    : tc?.days_checked_in?.length
                                      ? Math.min(...tc.days_checked_in)
                                      : undefined)
                            }
                            daysCheckedIn={
                                localDaysCheckedIn ?? tc?.days_checked_in
                            }
                            firstCheckInTz={tc?.timezone}
                            absencesLeft={livesLeft ?? undefined}
                            failed={hasFailed}
                        />
                    </div>
                ) : (
                    <div className="tovcic__content__vspacer" />
                )}

                {/* {(showReopenLink || true) && ( */}
                <div className="tovcic__content__reopen">
                    <a
                        // href={lastOpenedUrl!}
                        href={buildPartnerUrl(
                            partnerAppId,
                            progress?.code ?? "",
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="tovcic__content__reopen__link"
                    >
                        {t("TaskOverViewCheckInCampaign.navigateToAppX", {
                            appName:
                                (typeof task?.raw?.partner_name === "object" &&
                                    task?.raw?.partner_name?.[lang]) ??
                                (typeof task?.raw?.partner_name === "object" &&
                                    task?.raw?.partner_name?.en) ??
                                (typeof task?.raw?.partner_name === "string"
                                    ? task?.raw?.partner_name
                                    : "") ??
                                "app",
                        })}
                    </a>
                </div>
                {/* )} */}

                <div
                    className={[
                        "tovcic__content__btn",
                        stateClass,
                        btnDisabled ? "disabled" : "",
                    ].join(" ")}
                    aria-disabled={btnDisabled}
                    onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <Button3D
                        onClick={() => handleClick()}
                        className={[
                            "tovcic__content__btn__btn",
                            stateClass,
                            btnDisabled ? "disabled" : "",
                        ].join(" ")}
                        aria-busy={isSubmitting}
                        disabled={btnDisabled as any}
                        aria-disabled={btnDisabled}
                    >
                        <div className="tovcic__content__btn__btn__contents">
                            <p>{btnLabel}</p>
                            {verifyingUI || openingSoon ? (
                                <div className="tovcic__content__btn__btn__contents__loader">
                                    <CircularLoader />
                                </div>
                            ) : (
                                !btnDisabled &&
                                !canClaim && (
                                    <FontAwesomeIcon
                                        icon={faArrowUpRightFromSquare}
                                    />
                                )
                            )}
                        </div>
                    </Button3D>
                </div>
            </div>
        </div>
    );
}
